import ApiBase from './base/ApiBase';

class AiReviewClient extends ApiBase {
    private controllerName: string = 'AiReview';

    async GetConversation(talentId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.GetAsync(`${this.controllerName}/Talent/${talentId}/Conversation`, signal);
    }

    async GetSessions(talentId: string, signal?: AbortSignal): Promise<AiReviewSessionSummary[]> {
        return this.GetAsync(`${this.controllerName}/Talent/${talentId}/Sessions`, signal);
    }

    async GetSession(sessionId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.GetAsync(`${this.controllerName}/Session/${sessionId}`, signal);
    }

    async Start(talentId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.PostAsync(`${this.controllerName}/Talent/${talentId}/Start`, null, signal);
    }

    async Chat(talentId: string, request: AiReviewChatRequest, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.PostAsync(`${this.controllerName}/Talent/${talentId}/Chat`, request, signal);
    }

    async AcceptProposal(proposalId: string, rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.PostAsync(`${this.controllerName}/Proposal/${proposalId}/Accept`, { RowVersion: rowVersion ?? null }, signal);
    }

    async RejectProposal(proposalId: string, rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.PostAsync(`${this.controllerName}/Proposal/${proposalId}/Reject`, { RowVersion: rowVersion ?? null }, signal);
    }

    async UndoProposal(proposalId: string, rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
        return this.PostAsync(`${this.controllerName}/Proposal/${proposalId}/Undo`, { RowVersion: rowVersion ?? null }, signal);
    }

}

export type AiReviewMessageRole = 'user' | 'assistant' | 'system-event';
export type AiReviewProposalType = 'Proficiency' | 'DevelopmentGoal' | 'TemplateChange';
export type AiReviewProposalStatus = 'Pending' | 'Accepted' | 'Rejected' | 'Undone';

export interface AiReviewMessage {
    Id?: string;
    Role: AiReviewMessageRole;
    Content: string;
    CreatedDate?: string;
    Source?: 'Text' | 'Voice';
}

export interface AiReviewProposal {
    Id: string;
    Type: AiReviewProposalType;
    TargetProficiencyId?: string;
    TargetName: string;
    CurrentValue?: string | number | null;
    ProposedValue?: string | number | null;
    CurrentNotes?: string | null;
    ProposedNotes?: string | null;
    GoalTitle?: string | null;
    GoalNarrative?: string | null;
    Rationale: string;
    Evidence?: string | null;
    Status: AiReviewProposalStatus;
    CanApply: boolean;
    ConflictMessage?: string | null;
    RowVersion?: string | null;
}

export interface AiReviewWorkspace {
    SessionId: string;
    TalentId: string;
    TemplateRevisionId: string;
    IsAiConfigured: boolean;
    IsFinalized: boolean;
    ConfigurationMessage?: string | null;
    ContextSummary: string;
    Messages: AiReviewMessage[];
    Proposals: AiReviewProposal[];
}

export interface AiReviewSessionSummary {
    Id: string;
    TalentId: string;
    Status: 'Active' | 'Finalized';
    CreatedDate: string;
    FinalizedDate?: string | null;
    FinalizedAssessmentId?: string | null;
    TemplateName: string;
}

export interface AiReviewChatRequest {
    SessionId: string;
    Message: string;
    VoiceSessionId?: string | null;
}

export default AiReviewClient;
